<template>
  <div
    @click="goDetail(courseInfo.id)"
    class="course-card group"
    :class="{
      'h5-course-card': isH5 && needAdaptStyle,
      'electron-course-card': isElectron && needAdaptStyle,
      'pc-course-card': !isH5 && !isElectron,
    }"
  >
    <!-- 封面区域 16:9 -->
    <div class="cover-wrap relative w-full overflow-hidden" style="padding-top: 56.25%">
      <!-- 左上角标签 -->
      <div class="absolute top-3 left-3 right-3 z-[4] flex justify-between items-start pointer-events-none">
        <div>
          <span v-if="courseInfo.category_name" class="bg-slate-900/40 backdrop-blur-md text-white text-[10px] font-bold py-1 px-2.5 rounded-full border border-white/10 tracking-wide">
            {{ courseInfo.category_name }}
          </span>
        </div>
      </div>

      <!-- hover 暗色遮罩 -->
      <div v-if="!isH5" class="absolute top-0 left-0 w-full h-full bg-slate-900/0 transition-colors duration-300 z-[2] group-hover:bg-slate-900/20"></div>
      <!-- hover 播放按钮 -->
      <div v-if="!isH5" class="absolute top-0 left-0 w-full h-full flex items-center justify-center z-[3] opacity-0 scale-90 transition-all duration-300 group-hover:opacity-100 group-hover:scale-100">
        <div class="w-14 h-14 rounded-full bg-white/20 backdrop-blur-xl border border-white/40 flex items-center justify-center shadow-[0_8px_32px_rgba(0,0,0,0.2)]">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" class="text-white">
            <circle cx="12" cy="12" r="10" />
            <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
          </svg>
        </div>
      </div>

      <img
        class="absolute top-0 left-0 w-full h-full object-cover transition-transform duration-700 ease-out will-change-transform group-hover:scale-110"
        :src="getCoverUrl(courseInfo.cover_url)"
        alt=""
      />

      <!-- 底部来源标签（浮在图片上）- 仅桌面端 -->
      <div v-if="showOrgInfo && !isH5" class="absolute bottom-3 left-3 right-3 z-[4] transition-opacity duration-300 group-hover:opacity-0">
        <div class="inline-flex items-center gap-1.5 bg-white/95 dark:bg-slate-900/80 backdrop-blur-sm py-1 px-2.5 rounded-full border border-white/20 shadow-sm max-w-[90%]">
          <!-- 动态图标：个人用 Users，机构用 Building -->
          <svg v-if="isPersonalOrg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" class="text-brand-500 dark:text-brand-400 flex-shrink-0">
            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
            <circle cx="9" cy="7" r="4" />
            <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
          </svg>
          <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="text-brand-500 dark:text-brand-400 flex-shrink-0">
            <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
            <path d="M16 7V5a4 4 0 0 0-8 0v2" />
            <line x1="12" y1="12" x2="12" y2="16" />
          </svg>
          <span class="text-[10px] font-medium text-slate-700 dark:text-slate-200 truncate">{{ courseInfo.org_name }}</span>
        </div>
      </div>

      <CourseStatusTag
        :liveState="courseInfo.live_state || 0"
        :trialState="courseInfo.trial_state || 0"
      />
    </div>

    <!-- 内容区域 -->
    <div class="p-3 md:p-5 flex-1 flex flex-col bg-white dark:bg-slate-800 transition-colors duration-300">
      <!-- 标题 - 最多两行, 固定最小高度保持网格对齐 -->
      <h3
        class="course-title  md:text-[19px] font-bold text-slate-900 dark:text-slate-100 leading-snug md:leading-[1.4] mb-1 md:mb-3 min-h-[36px] md:min-h-[3.2rem] overflow-hidden text-ellipsis line-clamp-2 transition-colors duration-300 group-hover:text-brand-500 dark:group-hover:text-brand-400"
        :title="courseInfo.course_name"
      >{{ courseInfo.course_name }}</h3>

      <!-- 讲师/机构信息 -->
      <div v-if="showOrgInfo" class="flex items-center gap-1 mb-2 md:mb-6">
        <img class="w-2 h-2 md:w-5 md:h-5 rounded-full object-cover border border-slate-100 dark:border-slate-600 bg-slate-100" :src="handlerOrgLogo()" alt="" />
        <span class="text-[10px] md:text-sm text-slate-500 dark:text-slate-400 truncate max-w-[80px] md:max-w-[120px]">{{ courseInfo.org_name }}</span>
      </div>

      <!-- 底部：价格 + 学习人数 -->
      <div class="mt-auto card-footer-area">
        <!-- 价格行 -->
        <div class="flex items-end justify-between flex-wrap gap-x-2 gap-y-1 mb-2 md:mb-4">
          <div v-if="courseInfo.goods" class="flex items-end">
            <span v-if="courseInfo.goods.fee_type === 1" class="text-sm md:text-xl font-black text-brand-500 tracking-tight leading-none">免费</span>
            <Price
              v-else
              :size="priceSize"
              :activePrice="courseInfo.activetyPrice * 100"
              :originPrice="courseInfo.originPrice"
            />
          </div>
          <!-- 营销标识 - ml-auto 推到右侧 -->
          <p class="activity-mark ml-auto" v-if="activityText">{{ activityText }}</p>
        </div>
        <!-- 课时/报名信息 -->
        <div
          v-if="showOrgInfo"
          class="bg-gray-100/80 dark:bg-slate-700/50 rounded-lg px-3 py-2 md:py-2.5 flex items-center text-[10px] md:text-xs text-slate-500 dark:text-slate-400 font-medium transition-colors duration-300"
        >
          <span>{{ courseInfo.lesson_count }} 课时</span>
          <template v-if="courseInfo.goods && courseInfo.goods.show_enrollment_count === 1">
            <div class="w-px h-1 md:h-3 bg-gray-300 dark:bg-slate-600 mx-3"></div>
            <span>{{ million(courseInfo.enrollment_count) }} 人报名</span>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import courseCard from "@saas/components/src/mixins/courseCard";
import { isH5, Price, CourseStatusTag, isMobile, getCdnUrl } from "@saas/components/src";
import { million } from "@saas/components/src/utils/utils";
export default {
  mixins: [courseCard],
  props: {
    courseInfo: {
      type: Object,
      default: () => {
        return {};
      },
    },
    orgInfo: {
      type: Object,
      default: () => {
        return {};
      },
    },
    showOrgInfo: {
      type: Boolean,
      default: true,
    },
    needAdaptStyle: {
      type: Boolean,
      default: true,
    },
  },
  data: function () {
    return {
      million,
      isH5: isH5(),
      isMobile: isMobile(),
      isElectron: window.isElectron,
    };
  },
  methods: {
    getCoverUrl(url) {
      if (!url) return "";
      if (url.startsWith("data:") || url.startsWith("/img/") || url.startsWith("blob:")) {
        return url;
      }
      return url + "?imageView2/0/w/560";
    },
    goDetail(id) {
      let url = `/product-detail/${id}`;
      if (this.iosInReview) {
        this.$router.push(`${url}?inReview=1`);
      } else if (window.isElectron || this.isMobile) {
        this.$router.push(url);
      } else {
        window.open(url);
      }
    },
    handlerOrgLogo() {
      if (!this.orgInfo?.org_logo) {
        return getCdnUrl("/cdn/static/images/default_org_logo.png", "?imageView2/0/w/100", "comettech");
      } else {
        return this.orgInfo.org_logo + "?imageView2/0/w/100";
      }
    },
  },
  created() {
    this.iosInReview = this.$route.query.inReview === "1";
  },
  computed: {
    // 判断是个人还是企业机构，用于动态切换图标
    isPersonalOrg() {
      // 如果有 org_type 字段：1=个人, 2=企业
      if (this.orgInfo?.org_type) {
        return this.orgInfo.org_type === 1;
      }
      // 兜底：通过机构名称判断
      const name = this.courseInfo?.org_name || '';
      return !/公司|集团|企业|有限|科技|学院|学校|教育/.test(name);
    },
  },
  components: {
    Price,
    CourseStatusTag,
  },
};
</script>

<style lang="scss" scoped>
.course-card {
  background: var(--card-bg);
  border-radius: 0.75rem;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  position: relative;
  box-shadow: 0 2px 10px -4px rgba(0, 0, 0, 0.05);
  border: 1px solid #f1f5f9;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-6px);
    box-shadow: 0 20px 40px -4px rgba(0, 0, 0, 0.1);
    border-color: rgba($saas-theme-color, 0.2);
  }

  // 二行截断兜底样式
  .line-clamp-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    overflow: hidden;
    min-height: 3rem;
  }

  // 营销标签-亮色模式
  .activity-mark {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    line-height: 16px;
    color: $saas-price-color;
    height: 22px;
    border-radius: 6px;
    border: 1px solid rgba($color: $saas-price-color, $alpha: 0.3);
    background-color: rgba($color: $saas-price-color, $alpha: 0.06);
    padding: 0 6px;
    font-weight: 600;
    white-space: nowrap;
  }
}

@media (min-width: 768px) {
  .course-card {
    border-radius: 1.5rem;
  }
}

html.dark .course-card {
  background: var(--card-bg);
  box-shadow: none;
  border-color: rgba(255, 255, 255, 0.05);

  &:hover {
    box-shadow: 0 20px 40px -4px rgba(0, 0, 0, 0.5);
    border-color: rgba($saas-theme-color, 0.3);
  }

  .line-clamp-2 {
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .activity-mark {
    display: inline-flex;
    align-items: center;
    font-size: 12px;
    line-height: 16px;
    color: $saas-price-color;
    height: 24px;
    border-radius: 8px;
    border: 1px solid rgba($color: $saas-price-color, $alpha: 0.3);
    padding: 0 4px;
  }
}

// 客户端
.course-card.electron-course-card {
  border-radius: 1.25rem;

  // 内容区域
  > div:last-child {
    padding: 14px !important;
  }

  h3 {
    font-size: 14px !important;
    min-height: 40px !important;
    margin-bottom: 8px !important;
  }

  .card-footer-area {
    > div:last-child {
      font-size: 10px !important;
      padding: 6px 10px !important;
    }
  }
}

// h5
.course-card.h5-course-card {
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  &:hover {
    transition: none;
    transform: none;
  }
  transition: none;

  .cover-wrap {
    padding-top: 62.5% !important;
  }

  // 左上角标签
  .absolute.top-3 {
    top: 6px;
    left: 6px;
    right: 6px;
    span {
      font-size: 9px;
      padding: 2px 6px;
    }
  }

  // 内容区域 - override Tailwind rem-based padding/sizes
  > div:last-child {
    padding: 8px !important;
  }

  // Title
  h3 {
    font-size: 14px !important;
    line-height: 1.4 !important;
    min-height: 40px !important;
    margin-bottom: 6px !important;
    display: -webkit-box !important;
    -webkit-box-orient: vertical !important;
    -webkit-line-clamp: 2 !important;
    line-clamp: 2;
    overflow: hidden !important;
  }

  // price "免费"
  .text-brand-500.font-black {
    font-size: 14px !important;
  }

  // footer area (价格 + 课时)
  .card-footer-area {
    // price row
    > div:first-child {
      margin-bottom: 4px !important;
    }
    // stats badge (课时 | 人报名)
    > div:last-child {
      font-size: 9px !important;
      padding: 4px 8px !important;
    }
  }

  // activity mark
  .activity-mark {
    font-size: 10px;
    height: 18px;
    border-radius: 4px;
    padding: 0 3px;
  }
}
</style>
